import { Image } from "@/components/ui"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import type { SaleProp } from "@/modules/types/jobs/affiliate"

// ==================== Single SKU Selection ====================
interface SingleSkuSelectionProps {
  saleProps: SaleProp[]
  selectedSaleProp: Record<string, string>
  onSelect: (propValueId: string, propId: string) => void
  error?: string
  showTitle: boolean
  skus?: any[]
  skuHiddens?: any[]
}

export function SingleSkuSelection({
  saleProps,
  selectedSaleProp,
  onSelect,
  error,
  showTitle = true,
  skus = [],
  skuHiddens = []
}: SingleSkuSelectionProps) {
  if (saleProps.length === 0) return null

  const availableSkus = skus.filter((sku) => {
    const hiddenInfo = skuHiddens.find((h: any) => h.skuId === sku.skuId || h.id === sku.id)
    if (hiddenInfo?.isHidden) return false
    return true
  })

  const isValueHidden = (valueId: string, propIndex: number) => {
    if (availableSkus.length === 0) return false

    const context: Record<string, string> = {}

    for (let i = 0; i < propIndex; i++) {
      const p = saleProps[i]
      const selectedVal = selectedSaleProp[p.propId]
      if (selectedVal) {
        context[p.propId] = selectedVal
      }
    }

    context[saleProps[propIndex].propId] = valueId
    const relevantProps = Object.keys(context)

    return !availableSkus.some((sku) => {
      return relevantProps.every((pId) => {
        const pVal = context[pId]
        const sProp = sku.skuSaleProps?.find((sp: any) => sp.propId?.toString() === pId)
        return sProp && sProp.propValueId?.toString() === pVal
      })
    })
  }

  const handleSelect = (propValueId: string, propIndex: number) => {
    const currentProp = saleProps[propIndex]
    onSelect(propValueId, currentProp.propId)

    const nextContext: Record<string, string> = { ...selectedSaleProp }
    nextContext[currentProp.propId] = propValueId

    for (let i = propIndex + 1; i < saleProps.length; i++) {
      const p = saleProps[i]
      const currentVal = nextContext[p.propId]

      if (currentVal) {
        const checkContext: Record<string, string> = {}
        for (let k = 0; k < i; k++) {
          const pk = saleProps[k]
          const val = nextContext[pk.propId]
          if (val) checkContext[pk.propId] = val
        }
        checkContext[p.propId] = currentVal

        const relevantProps = Object.keys(checkContext)

        const isValid = availableSkus.some((sku) => {
          return relevantProps.every((pId) => {
            const pVal = checkContext[pId]
            const sProp = sku.skuSaleProps?.find((sp: any) => sp.propId?.toString() === pId)
            return sProp && sProp.propValueId?.toString() === pVal
          })
        })

        if (!isValid) {
          onSelect("", p.propId)
          delete nextContext[p.propId]
        }
      }
    }
  }

  return (
    <div className="mb-4">
      {showTitle && <h2 className="text-sm font-normal mb-3">Lựa chọn</h2>}
      <div className="space-y-6">
        {saleProps.map((prop, index) => (
          <div key={prop.propId} className="space-y-2">
            {prop.propName && <p className="text-xs font-normal">{prop.propName}</p>}
            <div className="grid-cols-4 grid gap-2">
              {prop.salePropValues?.map((value) => {
                const isSelected = selectedSaleProp[prop.propId] === value.propValueId?.toString()
                const isHidden = isValueHidden(value.propValueId?.toString() || "", index)

                return (
                  <button
                    key={value.propValueId}
                    type="button"
                    disabled={isHidden}
                    onClick={() =>
                      !isHidden && handleSelect(value.propValueId?.toString() || "", index)
                    }
                    className={cn(
                      "relative flex flex-col rounded-xl border p-2 w-full transition-all h-full min-h-[80px] justify-start gap-2 items-center mt-2",
                      isSelected
                        ? "border-primary border bg-primary-50/30"
                        : error
                          ? "border-red-500 bg-red-50/30"
                          : "border-gray-200 bg-gray-50/30",
                      isHidden &&
                        "opacity-50 cursor-not-allowed bg-gray-100 border-gray-100 text-gray-400"
                    )}
                  >
                    {value?.image && (
                      <div className="relative w-full">
                        <Image
                          src={value.image}
                          alt={value.propValue || ""}
                          className={cn(
                            "w-full aspect-square rounded-lg object-cover",
                            isHidden && "grayscale"
                          )}
                        />
                      </div>
                    )}
                    <span
                      className={cn(
                        "!text-xss font-semibold text-primary",
                        isHidden && "text-gray-400 font-normal"
                      )}
                    >
                      {value.propValue || "Phân loại 01"}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
      {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
    </div>
  )
}

// ==================== Multiple SKU Selection ====================
export interface ProductCampaignItem {
  id?: string
  title?: string
  thumbnail?: string
  saleProps?: SaleProp[]
}

interface MultipleSkuSelectionProps {
  productCampaignSaleProps: ProductCampaignItem[]
  selectedSkus: Record<string, Record<string, string>>
  onSelect: (productId: string, propId: string, propValueId: string) => void
  error?: string
}

export function MultipleSkuSelection({
  productCampaignSaleProps,
  selectedSkus,
  onSelect,
  error
}: MultipleSkuSelectionProps) {
  if (!productCampaignSaleProps?.length) return null

  return (
    <div className="mb-4">
      <h2 className="text-sm font-normal mb-3">Lựa chọn</h2>
      <div className="flex flex-col gap-1">
        {productCampaignSaleProps.map((item) => {
          const productId = item?.id || ""
          const productSelections = selectedSkus[productId] || {}

          return (
            <div key={productId} className="px-1">
              <div className="w-full py-3 px-2 bg-white rounded-xl my-1 shadow-sm border flex flex-col">
                {/* Product Header */}
                <div className="flex flex-row gap-4 w-full items-start">
                  <div className="w-20 h-20 rounded-xl overflow-hidden shadow-sm">
                    <img
                      src={item?.thumbnail}
                      alt={item?.title}
                      className="w-20 h-20 rounded-xl object-cover"
                    />
                  </div>
                  <div className="flex-1 justify-start">
                    <p className="text-sm font-semibold text-black leading-[22px] mb-1 w-[90%] line-clamp-3">
                      {item?.title}
                    </p>
                  </div>
                </div>

                {/* Sale Props Selection */}
                <div className="grid-cols-4 grid gap-2">
                  {item?.saleProps?.map((prop) => (
                    <div key={prop?.propId} className="mt-4">
                      <p className="text-sm font-semibold text-black">{prop?.propName?.trim()}</p>
                      <div className="flex flex-row flex-wrap gap-2 justify-start">
                        {prop.salePropValues?.map((value) => {
                          const isSelected =
                            productSelections[prop.propId] === value.propValueId?.toString()
                          return (
                            <button
                              key={value.propValueId}
                              type="button"
                              onClick={() =>
                                onSelect(
                                  productId,
                                  prop.propId,
                                  value.propValueId?.toString() || ""
                                )
                              }
                              className={cn(
                                "relative flex flex-col rounded-xl border p-2 w-full transition-all h-full min-h-[80px] justify-center items-center mt-2",
                                isSelected
                                  ? "border-primary border bg-primary-50/30"
                                  : error
                                    ? "border-red-500 bg-red-50/30"
                                    : "border-gray-200 bg-gray-50/30"
                              )}
                            >
                              {value?.image && (
                                <div className="relative w-full">
                                  <Image
                                    src={value.image}
                                    alt={value.propValue || ""}
                                    className="w-full aspect-square rounded-lg object-cover"
                                  />
                                </div>
                              )}
                              <span className="text-xss text-primary font-semibold">
                                {value.propValue || "Phân loại 01"}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
    </div>
  )
}

// ==================== Unified SKU Selection Component ====================
interface SkuSelectionProps {
  // Single SKU props
  saleProps?: SaleProp[]
  selectedSaleProp?: Record<string, string>
  onSelect?: (propValueId: string, propId: string) => void

  // Multiple SKU props
  productCampaignSaleProps?: ProductCampaignItem[]
  selectedSkus?: Record<string, Record<string, string>>
  onSelectMultiple?: (productId: string, propId: string, propValueId: string) => void

  // Common props
  error?: string
  showTitle?: boolean
  skus?: any[]
  skuHiddens?: any[]
}

export function SkuSelection({
  saleProps,
  selectedSaleProp,
  onSelect,
  productCampaignSaleProps,
  selectedSkus,
  onSelectMultiple,
  error,
  showTitle = true,
  skus,
  skuHiddens
}: SkuSelectionProps) {
  // Multiple SKU mode
  if (productCampaignSaleProps?.length && selectedSkus && onSelectMultiple) {
    return (
      <MultipleSkuSelection
        productCampaignSaleProps={productCampaignSaleProps}
        selectedSkus={selectedSkus}
        onSelect={onSelectMultiple}
        error={error}
      />
    )
  }

  // Single SKU mode
  if (saleProps && selectedSaleProp && onSelect) {
    return (
      <SingleSkuSelection
        saleProps={saleProps}
        selectedSaleProp={selectedSaleProp}
        onSelect={onSelect}
        error={error}
        showTitle={showTitle}
        skus={skus}
        skuHiddens={skuHiddens}
      />
    )
  }

  return null
}
